import { cx } from "@linaria/core";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import type { PageDescriptor } from "@pm2/api";
import { pm2CurrentAST } from "@pm2/content-engine";
import { pm2Header } from "@pm2/design-tokens";
import { type LibraryPage, getLibraryQuery } from "@pm2/library-page";
import { type PM2ConfigStore } from "@pm2/store";
import { units } from "@ui/css";
import { speechSelectors } from "@ui/kit";
import { prefetchQueryIfNeeded } from "@utils/common";

import * as styles from "./page.library.style";

type Props = {
  descriptor: PageDescriptor;
  LibraryPage: typeof LibraryPage;
};

function LibraryPageWrapper(props: Props): React.JSX.Element {
  useEffect(() => {
    pm2CurrentAST.current = undefined;
  }, []);

  return (
    <article
      className={cx(
        "gPortal-page",
        styles.wrapperCls,
        speechSelectors.enableSpeech.className,
      )}
      style={{
        minHeight: `calc(${units.vh(100)} - ${pm2Header.height.computed()})`,
      }}
    >
      <props.LibraryPage libraryId={props.descriptor.id} />
    </article>
  );
}

/**
 * fetch and render a library using the stand-alone LibraryComponent.
 */
export async function renderLibrary(
  queryClient: QueryClient,
  configStore: PM2ConfigStore,
  pageDescriptor: PageDescriptor,
): Promise<React.JSX.Element> {
  const { LibraryPage } = await import("@pm2/library-page");
  const configState = configStore.getState();

  await prefetchQueryIfNeeded(
    queryClient,
    getLibraryQuery(
      configState.config.id,
      pageDescriptor.id,
      configState.previewMode,
    ),
  );

  return (
    <LibraryPageWrapper descriptor={pageDescriptor} LibraryPage={LibraryPage} />
  );
}
